/**
 * BR-02 organization profile. Invoice details are editable after create -
 * "Still needed: billing_address" is not a dead end.
 */
import { useCallback, useEffect, useState, type FormEvent } from "react";
import { Badge, Button, Card, Eyebrow, StateBlock } from "@ui/primitives";
import { FormError, FormNotice, TextInput } from "@ui/forms";
import { fontSize } from "@design/index";
import { api, type ApiFieldErrors } from "@app/shared/api";
import { useToast } from "@app/shared/Toasts";

export interface Organization {
  id: string; name: string; slug: string;
  legal_name: string | null; trading_name?: string | null;
  country: string | null; website: string | null; industry: string | null;
  contact_email?: string | null;
  billing_address?: BillingAddress | null;
  tax_identifiers?: { vat?: string; company_number?: string } | null;
  verification_state: string;
  verification_notes?: string | null;
  my_role?: string;
}
export interface Readiness { ready: boolean; missing: string[]; note: string }

interface BillingAddress {
  line1: string; line2: string; city: string;
  region: string; postal_code: string; country: string;
}

const MISSING: Record<string, string> = {
  legal_name: "registered legal name",
  country: "country",
  billing_address: "billing address",
};

const emptyAddr = (country = ""): BillingAddress => ({
  line1: "", line2: "", city: "", region: "", postal_code: "", country,
});

function addrFrom(org: Organization | null): BillingAddress {
  const a = org?.billing_address;
  if (!a) return emptyAddr(org?.country ?? "");
  return {
    line1: a.line1 ?? "", line2: a.line2 ?? "", city: a.city ?? "",
    region: a.region ?? "", postal_code: a.postal_code ?? "",
    country: a.country || org?.country || "",
  };
}

export function OrganizationPanel({ onSaved }: { onSaved: () => void }) {
  const toast = useToast();
  const [loaded, setLoaded] = useState(false);
  const [org, setOrg] = useState<Organization | null>(null);
  const [readiness, setReadiness] = useState<Readiness | null>(null);
  const [form, setForm] = useState({
    name: "", legalName: "", tradingName: "", country: "",
    website: "", industry: "", contactEmail: "",
    vat: "", companyNumber: "",
  });
  const [addr, setAddr] = useState<BillingAddress>(emptyAddr());
  const [fields, setFields] = useState<ApiFieldErrors>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const apply = (next: Organization | null, ready: Readiness | null) => {
    setOrg(next);
    setReadiness(ready);
    if (!next) return;
    setForm({
      name: next.name ?? "",
      legalName: next.legal_name ?? "",
      tradingName: next.trading_name ?? "",
      country: next.country ?? "",
      website: next.website ?? "",
      industry: next.industry ?? "",
      contactEmail: next.contact_email ?? "",
      vat: next.tax_identifiers?.vat ?? "",
      companyNumber: next.tax_identifiers?.company_number ?? "",
    });
    setAddr(addrFrom(next));
  };

  const load = useCallback(async () => {
    const r = await api.get<{ organization: Organization | null; fundingReadiness: Readiness | null }>(
      "/onboarding/organization");
    if (r.ok) apply(r.data.organization, r.data.fundingReadiness);
    setLoaded(true);
  }, []);

  useEffect(() => { void load(); }, [load]);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true); setFields({}); setFormError(null);
    const payload = {
      name: form.name,
      legalName: form.legalName,
      tradingName: form.tradingName,
      country: form.country.toUpperCase(),
      website: form.website,
      industry: form.industry,
      contactEmail: form.contactEmail,
      billingAddress: { ...addr, country: (addr.country || form.country).toUpperCase() },
      taxIdentifiers: { vat: form.vat, company_number: form.companyNumber },
    };
    const res = org
      ? await api.patch<{ organization: Organization; fundingReadiness: Readiness | null }>(
        "/onboarding/organization", payload)
      : await api.post("/onboarding/organization", payload);
    setBusy(false);
    if (res.ok) {
      toast.ok(org ? "Organization saved." : "Organization created.");
      await load();
      onSaved();
      return;
    }
    if (res.fields) setFields(res.fields);
    else setFormError(res.message);
  }

  if (!loaded) return <StateBlock kind="loading" title="Loading your organization…" />;

  const tone = org?.verification_state === "VERIFIED" ? "ok"
    : org?.verification_state === "REJECTED" ? "danger" : "warn";

  return (
    <form onSubmit={(e) => void submit(e)} style={{ display: "grid", gap: 16, maxWidth: 680 }} noValidate>
      {formError ? <FormError message={formError} /> : null}

      {org ? (
        <Card padding={20} style={{ display: "grid", gap: 10 }}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center" }}>
            <Eyebrow>Staff verification</Eyebrow>
            <Badge tone={tone}>{org.verification_state.toLowerCase().replaceAll("_", " ")}</Badge>
          </div>
          <p style={{ margin: 0, fontSize: fontSize.body, color: "var(--muted)", lineHeight: 1.6 }}>
            Staff review your company against a checklist. You cannot flip this
            badge yourself. Completing the invoice details below is what you
            can do - funding does not wait on verification.
          </p>
          {org.verification_notes ? (
            <p style={{ margin: 0, fontSize: fontSize.body, lineHeight: 1.6 }}>{org.verification_notes}</p>
          ) : null}
          {org.my_role ? (
            <p style={{ margin: 0, fontSize: fontSize.label, color: "var(--faint)" }}>
              Your role: {org.my_role.toLowerCase()}
            </p>
          ) : null}
        </Card>
      ) : null}

      {readiness ? (
        <Card padding={20} style={{ display: "grid", gap: 8 }}>
          <Eyebrow>Before you can fund a campaign</Eyebrow>
          {readiness.ready
            ? <FormNotice message="Invoice details are complete. You can add funds on a campaign." />
            : (
              <p style={{ margin: 0, fontSize: fontSize.body, color: "var(--tone-warn-fg)" }}>
                Still needed: {readiness.missing.map((k) => MISSING[k] ?? k).join(", ")}.
                Fill them in below and save.
              </p>
            )}
          <p style={{ margin: 0, fontSize: fontSize.label, color: "var(--faint)" }}>{readiness.note}</p>
        </Card>
      ) : null}

      <Card padding={20} style={{ display: "grid", gap: 16 }}>
        <Eyebrow>{org ? "Organization" : "Create your organization"}</Eyebrow>
        <TextInput label="Organization name" required value={form.name} error={fields["name"]}
          onChange={(e) => setForm({ ...form, name: e.currentTarget.value })}
          hint="What creators will see on your briefs." />
        <TextInput label="Registered legal name" required value={form.legalName}
          error={fields["legalName"]}
          onChange={(e) => setForm({ ...form, legalName: e.currentTarget.value })}
          hint="Used for invoicing and payment. Must match your registration." />
        <TextInput label="Trading name" value={form.tradingName}
          onChange={(e) => setForm({ ...form, tradingName: e.currentTarget.value })} />
        <TextInput label="Country" required maxLength={2} value={form.country} error={fields["country"]}
          onChange={(e) => setForm({ ...form, country: e.currentTarget.value })}
          hint="Two-letter code, for example SG." />
        <TextInput label="Website" type="url" value={form.website}
          onChange={(e) => setForm({ ...form, website: e.currentTarget.value })} />
        <TextInput label="Industry" value={form.industry}
          onChange={(e) => setForm({ ...form, industry: e.currentTarget.value })} />
        <TextInput label="Invoice contact email" type="email" value={form.contactEmail}
          onChange={(e) => setForm({ ...form, contactEmail: e.currentTarget.value })} />
      </Card>

      <Card padding={20} style={{ display: "grid", gap: 16 }}>
        <Eyebrow>Billing address</Eyebrow>
        <p style={{ margin: 0, fontSize: fontSize.label, color: "var(--faint)", lineHeight: 1.5 }}>
          Required before you can pay. This is the address on the invoice,
          not staff verification.
        </p>
        <TextInput label="Address line 1" required value={addr.line1} error={fields["line1"]}
          onChange={(e) => setAddr({ ...addr, line1: e.currentTarget.value })} />
        <TextInput label="Address line 2" value={addr.line2}
          onChange={(e) => setAddr({ ...addr, line2: e.currentTarget.value })} />
        <div style={{ display: "grid", gap: 16, gridTemplateColumns: "repeat(2, minmax(0, 1fr))" }}>
          <TextInput label="City" required value={addr.city} error={fields["city"]}
            onChange={(e) => setAddr({ ...addr, city: e.currentTarget.value })} />
          <TextInput label="Region" value={addr.region}
            onChange={(e) => setAddr({ ...addr, region: e.currentTarget.value })} />
        </div>
        <div style={{ display: "grid", gap: 16, gridTemplateColumns: "repeat(2, minmax(0, 1fr))" }}>
          <TextInput label="Postal code" required value={addr.postal_code} error={fields["postal_code"]}
            onChange={(e) => setAddr({ ...addr, postal_code: e.currentTarget.value })} />
          <TextInput label="Address country" required maxLength={2}
            value={addr.country} error={fields["billingCountry"]}
            onChange={(e) => setAddr({ ...addr, country: e.currentTarget.value })}
            hint="Two-letter code." />
        </div>
        <TextInput label="VAT number" value={form.vat}
          onChange={(e) => setForm({ ...form, vat: e.currentTarget.value })} />
        <TextInput label="Company number" value={form.companyNumber}
          onChange={(e) => setForm({ ...form, companyNumber: e.currentTarget.value })} />
      </Card>

      <div>
        <Button type="submit" variant="primary" size="lg" disabled={busy}>
          {busy ? "Saving…" : org ? "Save organization" : "Create organization"}
        </Button>
      </div>
    </form>
  );
}
